// Are we online? One answer for the whole app.
//
// The browser's own `navigator.onLine` only knows whether there is a network
// interface up; a clinic router that has lost its uplink still counts as
// "online" to it. So the answer here is driven by what actually happens to
// requests: a request that never reaches the server marks us offline, and any
// reply that comes back marks us online again. The browser's online/offline
// events are folded in as well, because they are the fastest signal when the
// cable is pulled or the Wi-Fi drops.
//
// Pages use this to stop polling while offline (there is no point asking every
// 30 seconds and painting an error each time), to refresh the moment the
// connection returns, and to show the offline banner.

import { useEffect, useState } from 'react';

const listeners = new Set();

let online = typeof navigator === 'undefined' || navigator.onLine !== false;

const set = (next) => {
    if (next === online) return;
    online = next;
    listeners.forEach((listener) => {
        try {
            listener(next);
        } catch (error) {
            console.error('[connectivity] listener failed', error);
        }
    });
};

export const isOnline = () => online;

// Called with `true` / `false` whenever the state flips. Returns the
// unsubscribe function, so it slots straight into a useEffect.
export const subscribe = (listener) => {
    listeners.add(listener);
    return () => listeners.delete(listener);
};

// Reported by the API client.
export const reportNetworkFailure = () => set(false);
export const reportNetworkSuccess = () => set(true);

if (typeof window !== 'undefined' && window.addEventListener) {
    window.addEventListener('online', () => set(true));
    window.addEventListener('offline', () => set(false));
}

// React view of the same state.
export const useOnline = () => {
    const [state, setState] = useState(online);
    useEffect(() => subscribe(setState), []);
    return state;
};
